import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Heart, ArrowRight, Share2, Link2, Check, ShoppingBag } from "lucide-react";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import { getProduct } from "@/data/catalog";
import { ProductCard } from "@/components/ProductCard";

export const WishlistGrid = ({ compact = false, codes: override }) => {
  const { t } = useTranslation();
  const { codes } = useWishlist();
  const items = (override || codes).map(getProduct).filter(Boolean);
  if (items.length === 0) return (
    <div data-testid="wishlist-empty" className="rounded-3xl bg-[#f4f5f0] p-12 text-center">
      <Heart size={32} className="mx-auto text-black/30" />
      <p className="mt-3 font-display text-lg font-bold">{t("wishlist.empty")}</p>
      <p className="mt-1 text-sm text-black/60">{t("wishlist.emptySub")}</p>
      <Link to="/eshop" className="nv-btn-dark mt-6 !px-5 !py-2.5 text-xs">{t("nav.eshop")} <ArrowRight size={14} /></Link>
    </div>
  );
  return (
    <div data-testid="wishlist-grid" className={`grid gap-4 ${compact ? "sm:grid-cols-2 xl:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4"}`}>
      {items.map((p, i) => <ProductCard key={p.code} p={p} index={i} />)}
    </div>
  );
};

const ShareButton = ({ codes }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/wishlist?share=${codes.join(",")}`;
  const share = async () => {
    try {
      if (navigator.share) { await navigator.share({ title: "NINJAVITY", text: t("wishlist.shareText"), url }); return; }
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 2500);
      toast.success(t("wishlist.copied"), { description: url });
    } catch { toast(t("wishlist.copyFail"), { description: url }); }
  };
  return (
    <button data-testid="wishlist-share" onClick={share} disabled={codes.length === 0} className="nv-btn-dark !px-5 !py-2.5 text-xs">
      {copied ? <Check size={14} /> : <Share2 size={14} />} {copied ? t("wishlist.copied") : t("wishlist.share")}
    </button>
  );
};

const SharedView = ({ shared }) => {
  const { t } = useTranslation();
  const { addMany } = useWishlist();
  const { add, setOpen } = useCart();
  const items = shared.map(getProduct).filter(Boolean);
  const saveAll = () => { addMany(items.map((p) => p.code)); toast.success(t("wishlist.savedAll")); };
  const cartAll = () => { items.filter((p) => !p.soldout).forEach((p) => add(p)); toast.success(t("product.added")); setOpen(true); };
  return (
    <main className="nv-container py-12">
      <p className="nv-eyebrow inline-flex items-center gap-2"><Link2 size={14} /> {t("wishlist.sharedEyebrow")}</p>
      <h1 data-testid="wishlist-shared-title" className="mt-2 font-display text-4xl font-bold sm:text-5xl">{t("wishlist.sharedTitle")} <span className="text-black/40">({items.length})</span></h1>
      <p className="mt-3 max-w-xl text-black/60">{t("wishlist.sharedSub")}</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button data-testid="shared-save-all" onClick={saveAll} disabled={!items.length} className="nv-btn-lime !px-5 !py-2.5 text-xs"><Heart size={14} /> {t("wishlist.saveAll")}</button>
        <button data-testid="shared-cart-all" onClick={cartAll} disabled={!items.length} className="nv-btn-ghost !px-5 !py-2.5 text-xs"><ShoppingBag size={14} /> {t("wishlist.cartAll")}</button>
        <Link to="/wishlist" data-testid="shared-go-mine" className="nv-btn-ghost !px-5 !py-2.5 text-xs">{t("wishlist.title")} <ArrowRight size={14} /></Link>
      </div>
      <div className="mt-10"><WishlistGrid codes={shared} /></div>
    </main>
  );
};

export default function Wishlist() {
  const { t } = useTranslation();
  const { codes, count } = useWishlist();
  const [sp] = useSearchParams();
  const shared = (sp.get("share") || "").split(",").filter(Boolean);
  if (shared.length) return <SharedView shared={shared} />;
  return (
    <main className="nv-container py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="nv-eyebrow">NINJAVITY</p>
          <h1 data-testid="wishlist-title" className="mt-2 font-display text-4xl font-bold sm:text-5xl">{t("wishlist.title")} <span className="text-black/40">({count})</span></h1>
        </div>
        <ShareButton codes={codes} />
      </div>
      <div className="mt-10"><WishlistGrid /></div>
    </main>
  );
}
